import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { aiSetDescriptionApi } from '../services/api';

export interface AiDescMaterial {
  name: string;
  quantity?: number;
  kind?: 'part' | 'tool';
}

interface AISetDescriptionHelperProps {
  name: string;
  category: string;
  difficultyLevel: string;
  ageMin?: number;
  ageMax?: number;
  durationMinutes?: number;
  materials?: AiDescMaterial[];
  currentDescription?: string;
  onGenerated: (description: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
}

const AISetDescriptionHelper: React.FC<AISetDescriptionHelperProps> = ({
  name,
  category,
  difficultyLevel,
  ageMin = 6,
  ageMax = 100,
  durationMinutes = 120,
  materials = [],
  currentDescription = '',
  onGenerated,
  disabled = false,
  size = 'small',
  label = 'AI description',
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providerNote, setProviderNote] = useState<string | null>(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setProviderNote(null);
    try {
      const response = await aiSetDescriptionApi.generate({
        name,
        category,
        difficulty_level: difficultyLevel,
        recommended_age_min: ageMin,
        recommended_age_max: ageMax,
        estimated_duration_minutes: durationMinutes,
        materials: materials
          .filter((m) => m?.name)
          .map((m) => ({
            name: m.name,
            quantity: m.quantity,
            kind: m.kind || 'part',
          })),
        current_description: currentDescription,
      });
      const description = response.data?.description?.trim();
      if (!description) {
        throw new Error('No description returned');
      }
      onGenerated(description);
      if (response.data?.provider === 'fallback') {
        setProviderNote(
          response.data?.openai_configured
            ? 'Used local template (model unavailable).'
            : 'Used local template — set OPENAI_API_KEY on the API for richer AI copy.'
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to generate description'
      );
    } finally {
      setLoading(false);
    }
  };

  const canGenerate = Boolean(category && difficultyLevel) && !disabled && !loading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
      <Tooltip
        title={
          canGenerate || loading
            ? 'Generate a shop-ready description from name, category, difficulty, and parts'
            : 'Set category and difficulty first'
        }
      >
        <span>
          <Button
            size={size}
            variant="outlined"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon fontSize="small" />
              )
            }
            onClick={handleGenerate}
            disabled={!canGenerate}
          >
            {loading ? 'Generating…' : label}
          </Button>
        </span>
      </Tooltip>
      {error && (
        <Alert severity="error" sx={{ width: '100%', py: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {providerNote && !error && (
        <Typography variant="caption" color="text.secondary">
          {providerNote}
        </Typography>
      )}
    </Box>
  );
};

export default AISetDescriptionHelper;
